<script setup>
import { ref, defineExpose, onMounted, onUnmounted, watch } from 'vue'
import { gsap } from 'gsap'
import { Rive, Fit, Layout } from '@rive-app/canvas'
import Poleroid from '@components/global/accents/frames/Poleroid.vue'
import Tag from '@components/global/Tag.vue'
import { useGlobalStore } from '@stores/global.js'

const global = useGlobalStore()
const award = global.modal.payload

const canvas = ref(null)
const polaroidStack = ref(null)
let polaroidCtx

const setup = ref({
	type: 'dark',
	wide: true,
})

defineExpose({ setup })

onMounted(() => {
	const rive = new Rive({
		canvas: canvas.value,
		src: 'https://cdn.papercrowns.com/website/rive/banner-alt-2.riv',
		autoplay: true,
		autoBind: true,
		layout: new Layout({
			fit: Fit.Contain,
		}),
		stateMachines: 'crownsStateMachine',
		onLoad: () => {
			let vmi = rive.viewModelInstance
			const booleanProperty = vmi.boolean('isHovered')
			booleanProperty.value = true
		},
	})
})

onMounted(() => {
	if (!polaroidStack.value) return

	polaroidCtx = gsap.context(() => {
		const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

		// Each polaroid is "thrown" from off-screen bottom-right with an extra
		// spin that settles into its resting Tailwind rotation. GSAP reads the
		// current computed transform, so the rotate-[15deg]/[-10deg] classes are
		// preserved as the landing position.
		tl.from('.js-polaroid-1', {
			autoAlpha: 0,
			x: 500,
			y: 160,
			rotation: '+=70',
			duration: 0.85,
		}).from(
			'.js-polaroid-2',
			{
				autoAlpha: 0,
				x: 500,
				y: 160,
				rotation: '-=80',
				duration: 0.85,
			},
			'-=0.5',
		)
	}, polaroidStack.value)
})

onUnmounted(() => {
	polaroidCtx?.revert()
})
</script>

<template>
	<div class="h-full overflow-y-scroll pt-24">
		<div class="flex flex-col gap-8 px-10">
			<div class="flex flex-col items-start gap-5">
				<div class="w-52">
					<div class="relative flex items-center justify-center">
						<img class="absolute z-10 mb-[25%] max-h-[30%] max-w-[40%]" :src="award.logo" alt="" />
						<canvas class="w-full grayscale transition-all duration-300 group-hover:grayscale-0" height="512" width="512" ref="canvas"></canvas>
						<!-- <img class="brightness-150 grayscale transition-all duration-300 group-hover:brightness-100 group-hover:grayscale-0" src="https://cdn.papercrowns.com/website/images/elements/etches/banner-gold.png" alt="" /> -->
					</div>
				</div>
				<h4>{{ award.name }}</h4>
				<div class="flex gap-1.5">
					<Tag v-for="year in award.years">{{ year }}</Tag>
				</div>
			</div>
			<div class="flex flex-col gap-5">
				<div class="flex items-center gap-3" v-for="highlight in award.highlights">
					<img class="h-10" src="https://cdn.papercrowns.com/website/images/elements/etches/homestead.svg" alt="Homestead Icon" />
					<div class="text-crowns-off-white">{{ highlight }}</div>
				</div>
			</div>
			<div>
				<p>{{ award.description }}</p>
			</div>
		</div>

		<div class="flex flex-col items-end overflow-hidden pt-5" ref="polaroidStack">
			<Poleroid class="js-polaroid-1 mt-10 rotate-[15deg]" :src="award.gallery_images[1]" alt="Esports Awards" v-if="award.gallery_images?.[1]" />
			<Poleroid class="js-polaroid-2 -mt-20 mb-10 mr-52 rotate-[-10deg]" :src="award.gallery_images[0]" alt="Esports Awards" v-if="award.gallery_images?.[0]" />
		</div>
	</div>
</template>
